import Modal from '@components/common/Modal'

/**
 * "We couldn't load your report list" — the queue picker's catalog read, when it
 * did not resolve (CC-53132).
 *
 * ## Why this is its own component rather than a branch inside QueueReportModal
 *
 * `QueueReportModal` renders the picker `<select>` before any error branch it has,
 * so there is no slot inside it that can carry this. It would also be the wrong
 * noun: the report LIST failed, not one report's filters — the modal's own
 * `columnsError` surface is about a single report's definition and says so.
 *
 * ## Why it is ONE component rather than a branch in each host
 *
 * Two hosts open this picker (`ReportQueue` and `ReportSchedules`), and this repo
 * has a written rule against a second copy of a safety surface: a later fix lands
 * on one copy and silently misses the other. That has already happened here with
 * two CB pickers and two CSV header heuristics. One component, two call sites.
 *
 * Which was aspirational when written and is only true as of 2026-09-02. CC-53132
 * shipped this component with ONE call site — `ReportQueue` — because
 * `ReportSchedules` rendered `QueueReportModal` unconditionally. So for three weeks
 * the Schedules page opened an empty picker on a catalog that never loaded, which is
 * the exact lie this component exists to prevent, on one of the two hosts named
 * above. Sharing a component is not the same as being wired to it: the anti-second-
 * copy rule protects against divergent COPIES and says nothing about a host that
 * never calls the shared one. If you add a third host, grep this file's importers
 * and check each one renders this branch — do not infer it from the import.
 *
 * ## Why a modal at all, rather than just not opening one
 *
 * Silence is the defect. The user clicked "Queue a report" and something must
 * answer; an empty picker offering only "Choose a report…" is the lie this ticket
 * is about. Same `Modal` shell as the real thing, so the focus trap, Escape
 * handling and labelling are intact.
 *
 * @param {'blocked'|'failed'} state  which unresolved state this is
 * @param {Error} [error]             the error, when there is one to show
 * @param {() => void} onRetry        re-issue the catalog read
 * @param {() => void} onClose        dismiss
 */
const QueueCatalogUnreadable = ({ state, error, onRetry, onClose }) => (
  <Modal
    title="Queue report"
    onClose={onClose}
    maxWidth="max-w-xl"
    footer={(
      <>
        <span className="flex-1" />
        <button type="button" onClick={onClose} className="btn-cb-toolbar">Close</button>
        <button type="button" onClick={onRetry} className="btn-cb-primary">Try again</button>
      </>
    )}
  >
    <div role="alert" className="px-5 py-5">
      <p className="text-sm font-semibold text-gray-900">
        {state === 'blocked'
          ? 'Can’t reach the server, so we haven’t loaded your report list.'
          : 'We couldn’t load your report list.'}
      </p>
      {state === 'failed' && error?.message && (
        <p className="mt-1.5 text-[12.5px] text-gray-500">{error.message}</p>
      )}
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-gray-500">
        This is not an empty catalog — there is nothing to choose from because the list never
        arrived, so anything queued from here would be a guess. Your existing runs and schedules
        are unaffected.
      </p>
    </div>
  </Modal>
)

export default QueueCatalogUnreadable
